// oxlint-disable react-hooks/exhaustive-deps
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { useEffect, useState } from 'react';
import { faExclamationCircle } from '@fortawesome/pro-light-svg-icons';
import { useTranslation } from 'react-i18next';
import { faAnglesUpDown } from '@fortawesome/pro-regular-svg-icons';
import { Button, Popover, PopoverTrigger } from '@e2n/ui';
import { useStandortContext } from '@/features/standortContext/StandortContext';
import { getColorByOption } from '@/domain/arbeitszeiten/components/Comboboxen/ArbeitsbereichCombobox/getColorByOption';
import { ArbeitsbereichComboboxPopoverContent } from '@/domain/arbeitszeiten/components/Comboboxen/ArbeitsbereichCombobox/ArbeitsbereichComboboxPopoverContent';
import { cn } from '@/lib/utils';
import { Markierung } from '@/domain/arbeitszeiten/components/Markierung';
import { Arbeitsbereich, Mitarbeiter } from '@/domain/arbeitszeiten/entities';
import { useArbeitsbereiche } from '@/domain/arbeitszeiten/hooks';

// eslint-disable-next-line max-lines-per-function complexity
export function ArbeitsbereichSelect({
  onSelect,
  defaultValue = [],
  mitarbeiter,
  minimunSelect = 0,
  open,
  onOpenChange,
}: {
  onSelect: (arbeitsbereiche: Arbeitsbereich[]) => void;
  defaultValue?: Arbeitsbereich[];
  mitarbeiter?: Mitarbeiter;
  minimunSelect?: number;
  open?: boolean;
  onOpenChange?: (open: boolean) => void;
}) {
  const {
    currentStandort: { id: standortId },
  } = useStandortContext();
  const { arbeitsbereiche, error } = useArbeitsbereiche(standortId);
  const { t } = useTranslation();

  const [selectedArbeitsbereiche, setSelectedArbeitsbereiche] =
    useState<Arbeitsbereich[]>(defaultValue);

  useEffect(() => {
    if (mitarbeiter && mitarbeiter.arbeitsbereich) {
      const mitarbeiterArbeitsbereich = mitarbeiter.arbeitsbereich;
      if (
        !selectedArbeitsbereiche.some(
          (ab) => ab.id === mitarbeiterArbeitsbereich.id,
        )
      ) {
        setSelectedArbeitsbereiche([mitarbeiterArbeitsbereich]);
        onSelect([mitarbeiterArbeitsbereich]);
      }
    }
  }, [mitarbeiter]);

  if (error) {
    return (
      <Button
        variant="ghost"
        role="combobox"
        aria-expanded={open}
        className="w-[240px] justify-between bg-white border border-neutrals-300">
        {selectedArbeitsbereiche.length > 0 ? (
          <div className="flex gap-2 items-center">
            <Markierung
              color={getColorByOption(
                selectedArbeitsbereiche[selectedArbeitsbereiche.length - 1].id,
                arbeitsbereiche ?? [],
              )}
            />
            {selectedArbeitsbereiche[selectedArbeitsbereiche.length - 1].name}
          </div>
        ) : (
          t('domain.arbeitszeiten.uebersicht.arbeitsbereichfilter.placeholder')
        )}

        <FontAwesomeIcon
          icon={faExclamationCircle}
          className="text-primary-red-500"
        />
      </Button>
    );
  }

  return (
    <Popover
      open={open}
      onOpenChange={(newOpen) => {
        if (onOpenChange) {
          onOpenChange(newOpen);
        }
      }}>
      <PopoverTrigger asChild>
        <Button
          role="combobox"
          aria-expanded={open}
          variant="outline-secondary"
          className={cn(
            'w-[275px] justify-between font-normal [&>svg]:text-neutrals-900',
            selectedArbeitsbereiche ? 'text-neutrals-900' : 'text-neutrals-700',
          )}>
          {selectedArbeitsbereiche.length > 0 ? (
            <div className="flex gap-2 items-baseline">
              <Markierung
                color={getColorByOption(
                  selectedArbeitsbereiche[selectedArbeitsbereiche.length - 1]
                    .id,
                  arbeitsbereiche ?? [],
                )}
              />
              <span className="truncate w-[130px] text-start text-sm">
                {
                  selectedArbeitsbereiche[selectedArbeitsbereiche.length - 1]
                    .name
                }
              </span>
              {selectedArbeitsbereiche.length > 1 && (
                <span className="text-neutrals-700">
                  {'+' + (selectedArbeitsbereiche.length - 1) + ' weitere'}
                </span>
              )}
            </div>
          ) : (
            <span className="w-40 font-normal text-neutrals-700">
              {t(
                'domain.arbeitszeiten.uebersicht.arbeitsbereichfilter.placeholder',
              )}
            </span>
          )}

          <div className="flex items-baseline ps-2">
            <FontAwesomeIcon icon={faAnglesUpDown} />
          </div>
        </Button>
      </PopoverTrigger>
      <ArbeitsbereichComboboxPopoverContent
        arbeitsbereiche={arbeitsbereiche ?? []}
        onSelect={onSelect}
        selectedArbeitsbereiche={selectedArbeitsbereiche}
        setSelectedArbeitsbereiche={setSelectedArbeitsbereiche}
        minimunSelect={minimunSelect}
        onOpenChange={onOpenChange}
      />
    </Popover>
  );
}
